import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Spinner, X, CaretLeft, CaretRight, Check, CheckCircle, House, Phone,
  PhoneCall, Signature, MapPin, Users, EnvelopeSimple, ListChecks, Warning,
  Sparkle, Trash,
} from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { rememberBack } from "@/utils/propertyBackTracker";

/**
 * Calendar/agenda UI primitives — shared between TabOggi (daily view) and
 * the full WeekView. Extracted from Agenda.jsx (iter 100c refactor).
 *
 * Exports:
 *   - EVENT_TYPES (constant: per-type styling/icon)
 *   - formatHourMinute(iso): "10:30"
 *   - todayISOInRome(): "2026-05-04"
 *   - weekdayLabel(dateStr): "Oggi"|"Domani"|"Lunedì 6 Maggio"
 *   - QuickAddModal: prompt-style modal to create an appointment
 *   - AgendaEventRow: single event row (compact)
 *   - WeekView: 7-day grid with event tiles per day
 */

// ── Event type styling ───────────────────────────────────────────
export const EVENT_TYPES = {
  sopralluogo:    { label: "Sopralluogo",  Icon: House,       color: "bg-[#7C8B6E] text-white",      dot: "bg-[#7C8B6E]" },
  visita:         { label: "Visita",       Icon: Users,       color: "bg-blue-600 text-white",        dot: "bg-blue-600" },
  firma:          { label: "Firma",        Icon: Signature,   color: "bg-purple-600 text-white",      dot: "bg-purple-600" },
  chiamata:       { label: "Chiamata",     Icon: PhoneCall,   color: "bg-amber-500 text-white",       dot: "bg-amber-500" },
  scadenza:       { label: "Scadenza",     Icon: Warning,     color: "bg-red-600 text-white",         dot: "bg-red-600" },
  riunione:       { label: "Riunione",     Icon: Users,       color: "bg-indigo-600 text-white",      dot: "bg-indigo-600" },
  comunicazione:  { label: "Messaggio",    Icon: EnvelopeSimple, color: "bg-teal-600 text-white",     dot: "bg-teal-600" },
  promemoria:     { label: "Promemoria",   Icon: ListChecks,  color: "bg-[#3F3D38] text-white",       dot: "bg-[#3F3D38]" },
};

export function formatHourMinute(iso) {
  if (!iso) return "--:--";
  const d = new Date(iso);
  return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
}

export function todayISOInRome() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year").value;
  const m = parts.find((p) => p.type === "month").value;
  const d = parts.find((p) => p.type === "day").value;
  return `${y}-${m}-${d}`;
}

export function weekdayLabel(dateStr) {
  // Noon UTC avoids DST boundary issues when formatting back in Europe/Rome
  const d = new Date(dateStr + "T12:00:00Z");
  return d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Rome" });
}

// ── Quick add appointment modal ──────────────────────────────────
export function QuickAddModal({ open, onClose, defaultDate, onCreated }) {
  const [title, setTitle] = useState("");
  const [dueText, setDueText] = useState("");
  const [eventType, setEventType] = useState("sopralluogo");
  const [location, setLocation] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(""); setDueText(""); setEventType("sopralluogo"); setLocation("");
    }
  }, [open]);

  const save = async () => {
    if (!title.trim()) { toast.error("Serve un titolo"); return; }
    setSaving(true);
    try {
      await api.post("/agenda/todos", {
        title: title.trim(),
        due_text: dueText.trim() || (defaultDate ? `${defaultDate} 09:00` : null),
        event_type: eventType,
        location: location.trim() || null,
        priority: eventType === "scadenza" ? "urgente" : "media",
      });
      toast.success("Aggiunto");
      onCreated();
      onClose();
    } catch (e) {
      toast.error(apiError(e, "Errore"));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end lg:items-center justify-center p-3 lg:p-6" data-testid="quick-add-modal">
      <div className="w-full max-w-md bg-white rounded-t-2xl lg:rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E5DE]">
          <div className="text-[15px] font-semibold text-[#1C1C1A]">Aggiungi all'agenda</div>
          <button onClick={onClose} className="p-1.5 text-[#9A968B] hover:text-[#1C1C1A]" data-testid="quick-add-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1">Titolo</label>
            <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Es. Sopralluogo Ferrari Via Brera"
              className="w-full px-3 py-2 border border-[#E8E5DE] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
              data-testid="qa-title" />
          </div>
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1">Tipo</label>
            <div className="flex flex-wrap gap-1">
              {Object.entries(EVENT_TYPES).map(([k, v]) => (
                <button key={k} onClick={() => setEventType(k)}
                  className={`px-2.5 py-1 text-[11.5px] font-medium rounded-full transition-colors ${eventType === k ? v.color : "bg-[#FAFAF7] text-[#6B6A65] border border-[#E8E5DE]"}`}
                  data-testid={`qa-type-${k}`}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1">Quando</label>
            <input value={dueText} onChange={(e) => setDueText(e.target.value)}
              placeholder='es. "domani alle 10" o "venerdì pomeriggio"'
              className="w-full px-3 py-2 border border-[#E8E5DE] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
              data-testid="qa-when" />
            <div className="text-[10.5px] text-[#9A968B] mt-1">Lascia vuoto per domani mattina 09:00</div>
          </div>
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1">Luogo (opzionale)</label>
            <input value={location} onChange={(e) => setLocation(e.target.value)}
              placeholder="es. Via Brera 12, Milano"
              className="w-full px-3 py-2 border border-[#E8E5DE] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
              data-testid="qa-location" />
          </div>
          <button onClick={save} disabled={saving}
            className="w-full py-2.5 bg-[#7C8B6E] hover:bg-[#6B7A5D] disabled:opacity-60 text-white font-medium rounded-md flex items-center justify-center gap-1.5 transition-colors"
            data-testid="qa-save">
            {saving ? <Spinner size={14} className="animate-spin" /> : <Check size={14} weight="bold" />}
            Aggiungi
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Daily Agenda Event Row ───────────────────────────────────────
export function AgendaEventRow({ todo, onUpdate }) {
  const ev = EVENT_TYPES[todo.event_type] || EVENT_TYPES.promemoria;
  const { Icon } = ev;
  const time = formatHourMinute(todo.due_at);
  const isDone = todo.status === "done";
  const isPast = todo.due_at && new Date(todo.due_at) < new Date() && !isDone;

  const toggle = async () => {
    try {
      await api.patch(`/agenda/todos/${todo.id}`, { status: isDone ? "open" : "done" });
      onUpdate();
    } catch (e) { toast.error(apiError(e, "Errore")); }
  };

  // iter v32 r1 — DELETE definitivo dall'agenda (utente M1671: «non posso
  // neanche eliminarlo nell'agenda proprio»). Conferma toast con action
  // button rosso così evitiamo cancellazioni accidentali su mobile.
  const remove = () => {
    toast(
      "Eliminare questo appuntamento dall'agenda?",
      {
        description: "Verrà rimosso definitivamente. Non si può annullare.",
        action: {
          label: "Elimina",
          onClick: async () => {
            try {
              await api.delete(`/agenda/todos/${todo.id}`);
              toast.success("Appuntamento eliminato");
              onUpdate();
            } catch (e) { toast.error(apiError(e, "Errore eliminazione")); }
          },
        },
        cancel: { label: "Annulla", onClick: () => {} },
        duration: 8000,
      },
    );
  };

  const mapsHref = todo.location ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(todo.location)}` : null;
  const telHref = todo.contact?.phone ? `tel:${todo.contact.phone}` : null;
  // iter 313 r10 — WhatsApp deep-link diretto dal task (richiesta utente:
  // "magari allora fai possibile apertura di WhatsApp").
  const phoneClean = todo.contact?.phone ? todo.contact.phone.replace(/[^\d+]/g, "").replace(/^\+/, "") : "";
  const waHref = phoneClean ? `https://wa.me/${phoneClean}` : null;
  const isAuto = todo.auto_generated_from === "lead";

  return (
    <div className={`flex gap-3 ${isDone ? "opacity-50" : ""}`} data-testid={`agenda-event-${todo.id}`}>
      {/* Time column */}
      <div className="w-14 shrink-0 pt-1 text-right">
        <div className={`text-[14px] font-serif tabular-nums ${isPast && !isDone ? "text-red-600" : "text-[#1C1C1A]"}`}>{time}</div>
      </div>
      {/* Dot + line */}
      <div className="flex flex-col items-center shrink-0 pt-1.5">
        <div className={`w-2.5 h-2.5 rounded-full ${ev.dot} ring-4 ring-[#FAFAF7]`}></div>
      </div>
      {/* Content */}
      <div className="flex-1 min-w-0 pb-3">
        <div className={`rounded-lg border p-3 transition-all ${isDone ? "bg-[#F5F4F0] border-[#E8E5DE]" : isPast ? "bg-red-50 border-red-200" : "bg-white border-[#E8E5DE] hover:shadow-sm"}`}>
          <div className="flex items-start gap-2">
            <button onClick={toggle} className="shrink-0 mt-0.5" data-testid={`agenda-toggle-${todo.id}`} aria-label="toggle">
              {isDone ? (
                <CheckCircle size={18} weight="fill" className="text-[#7C8B6E]" />
              ) : (
                <div className="w-[18px] h-[18px] rounded-full border-2 border-[#C8C5BD] hover:border-[#7C8B6E] transition-colors" />
              )}
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.08em] font-bold px-1.5 py-0.5 rounded ${ev.color}`}>
                  <Icon size={10} weight="fill" /> {ev.label}
                </span>
                {todo.priority === "urgente" && <span className="text-[9px] uppercase tracking-[0.08em] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200">URGENTE</span>}
                {/* iter 313 r10 — Badge "Da lead" per task auto-generati dal
                    contact form pubblico. Spiega all'agente perché c'è un
                    promemoria "Richiamare X" che non ha messo lui.
                    iter v29 r221 — User M1547: «la pillola è troppo grande,
                    occupa tutta la riga». Compatta: solo "WEB" + Sparkle icon. */}
                {isAuto && (
                  <span
                    className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.06em] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                    title="Promemoria creato quando il cliente ha lasciato i contatti sul tuo annuncio pubblico"
                    data-testid={`agenda-auto-badge-${todo.id}`}
                  >
                    <Sparkle size={8} weight="fill" /> WEB
                  </span>
                )}
              </div>
              <div className={`text-[13.5px] mt-1 ${isDone ? "line-through text-[#9A968B]" : "text-[#1C1C1A]"}`}>{todo.title}</div>
              {(todo.contact?.name || todo.location || todo.property?.title) && (
                <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[11.5px] text-[#6B6A65]">
                  {todo.contact?.name && <span className="inline-flex items-center gap-1"><Users size={11} /> {todo.contact.name}</span>}
                  {todo.location && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {todo.location}</span>}
                  {todo.property?.title && <Link to={`/dashboard/property/${todo.property_id}`} onClick={() => rememberBack(todo.property_id, "Agenda", "/dashboard/agenda")} className="inline-flex items-center gap-1 text-[#7C8B6E] hover:underline"><House size={11} /> {todo.property.title}</Link>}
                </div>
              )}
              {(mapsHref || telHref || waHref) && !isDone && (
                <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                  {mapsHref && (
                    <a href={mapsHref} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] bg-[#FAFAF7] hover:bg-[#EDEAE2] border border-[#E8E5DE] rounded-md px-2 py-1 transition-colors" data-testid={`agenda-maps-${todo.id}`}>
                      <MapPin size={11} /> Indicazioni
                    </a>
                  )}
                  {telHref && (
                    <a href={telHref}
                      className="inline-flex items-center gap-1 text-[11px] bg-[#FAFAF7] hover:bg-[#EDEAE2] border border-[#E8E5DE] rounded-md px-2 py-1 transition-colors" data-testid={`agenda-call-${todo.id}`}>
                      <Phone size={11} /> Chiama
                    </a>
                  )}
                  {waHref && (
                    <a href={waHref} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] bg-[#25D366]/12 hover:bg-[#25D366]/22 text-[#128C7E] border border-[#25D366]/30 rounded-md px-2 py-1 font-semibold transition-colors" data-testid={`agenda-wa-${todo.id}`}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.768.966-.941 1.165-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      WhatsApp
                    </a>
                  )}
                </div>
              )}
            </div>
            {/* iter v32 r1 — Delete button visibile sempre, top-right del
                card (sibling del content, dentro la flex-row). Gray default,
                rosso al hover. */}
            <button
              onClick={remove}
              className="shrink-0 mt-0.5 -mr-1 p-1.5 text-[#9A968B] hover:text-red-600 transition-colors"
              title="Elimina dall'agenda"
              aria-label="elimina appuntamento"
              data-testid={`agenda-delete-${todo.id}`}
            >
              <Trash size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tab Oggi — agenda giornaliera ───────────────────────────────
// ── Weekly view ────────────────────────────────────────────────
export function WeekView({ onUpdate }) {
  const [start, setStart] = useState(() => {
    const today = todayISOInRome();
    const [y, m, d] = today.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    const wd = dt.getUTCDay(); // 0=Sun
    const diff = (wd + 6) % 7;  // Monday = 0
    dt.setUTCDate(dt.getUTCDate() - diff);
    return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
  });
  const [week, setWeek] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/agenda/week?start=${start}`);
      setWeek(data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [start]);

  const shiftWeek = (delta) => {
    const [y, m, d] = start.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d + delta * 7));
    setStart(`${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`);
  };

  const goThisWeek = () => {
    const today = todayISOInRome();
    const [y, m, d] = today.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    const wd = dt.getUTCDay();
    const diff = (wd + 6) % 7;
    dt.setUTCDate(dt.getUTCDate() - diff);
    setStart(`${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`);
  };

  if (!week) return <div className="py-10 text-center"><Spinner size={18} className="animate-spin mx-auto text-[#7C8B6E]" /></div>;

  const WEEKDAY_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

  return (
    <div className="space-y-4" data-testid="week-view">
      {/* Header */}
      <div className="bg-white border border-[#E8E5DE] rounded-lg p-3 flex items-center gap-2">
        <button onClick={() => shiftWeek(-1)} className="p-1.5 text-[#6B6A65] hover:text-[#1C1C1A] hover:bg-[#FAFAF7] rounded-md transition-colors" data-testid="week-prev" aria-label="settimana precedente">
          <CaretLeft size={16} />
        </button>
        <div className="flex-1 text-center">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A968B] font-semibold">Settimana</div>
          <div className="text-[15px] font-serif text-[#1C1C1A]" data-testid="week-label">
            {new Date(week.start + "T12:00:00Z").toLocaleDateString("it-IT", { day: "numeric", month: "short", timeZone: "Europe/Rome" })}
            {" — "}
            {new Date(week.end + "T12:00:00Z").toLocaleDateString("it-IT", { day: "numeric", month: "short", timeZone: "Europe/Rome" })}
          </div>
        </div>
        <button onClick={() => shiftWeek(1)} className="p-1.5 text-[#6B6A65] hover:text-[#1C1C1A] hover:bg-[#FAFAF7] rounded-md transition-colors" data-testid="week-next" aria-label="settimana successiva">
          <CaretRight size={16} />
        </button>
        <button onClick={goThisWeek} className="ml-1 text-[11px] text-[#7C8B6E] hover:underline font-medium" data-testid="week-today">
          Questa settimana
        </button>
      </div>

      {/* 7 day columns */}
      <div className="grid grid-cols-7 gap-1.5" data-testid="week-grid">
        {week.days.map((day, i) => {
          const [, , dd] = day.date.split("-");
          return (
            <div
              key={day.date}
              className={`bg-white border rounded-lg overflow-hidden flex flex-col ${day.is_today ? "border-[#7C8B6E] ring-1 ring-[#7C8B6E]" : "border-[#E8E5DE]"}`}
              data-testid={`week-day-${day.date}`}
            >
              <div className={`px-2 py-1.5 text-center ${day.is_today ? "bg-[#7C8B6E] text-white" : "bg-[#FAFAF7] text-[#6B6A65]"}`}>
                <div className="text-[9px] uppercase tracking-[0.15em] font-semibold">{WEEKDAY_SHORT[i]}</div>
                <div className={`font-serif ${day.is_today ? "text-[20px]" : "text-[17px]"} leading-none mt-0.5`}>{parseInt(dd, 10)}</div>
              </div>
              <div className="flex-1 p-1.5 space-y-1 min-h-[180px]">
                {day.events.length === 0 ? (
                  <div className="text-[10px] text-[#9A968B] text-center pt-6">—</div>
                ) : (
                  day.events.map((e) => {
                    const ev = EVENT_TYPES[e.event_type] || EVENT_TYPES.promemoria;
                    const { Icon } = ev;
                    const isDone = e.status === "done";
                    return (
                      <button
                        key={e.id}
                        onClick={() => onUpdate(e.due_at.slice(0, 10))}
                        className={`w-full text-left rounded px-1.5 py-1 text-[10.5px] transition-all ${isDone ? "opacity-50" : ""} ${ev.color} hover:brightness-95`}
                        data-testid={`week-event-${e.id}`}
                        title={`${e.title} ${e.location ? "· " + e.location : ""}`}
                      >
                        <div className="flex items-center gap-1">
                          <Icon size={9} weight="fill" />
                          <span className="font-mono text-[10px] font-semibold">
                            {String(e.hour).padStart(2, "0")}:{String(e.minute).padStart(2, "0")}
                          </span>
                        </div>
                        <div className={`truncate leading-tight mt-0.5 ${isDone ? "line-through" : ""}`}>{e.title}</div>
                        {e.contact_name && <div className="truncate text-[9.5px] opacity-80">{e.contact_name}</div>}
                      </button>
                    );
                  })
                )}
              </div>
              <div className="text-center text-[9.5px] text-[#9A968B] py-1 border-t border-[#F0EEE7]">
                {day.count > 0 ? `${day.count} evento${day.count > 1 ? "i" : ""}` : "libero"}
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-center text-[11px] text-[#9A968B]">
        Tocca un evento per aprire il giorno corrispondente
      </div>
    </div>
  );
}

